/**
 * Adaptive quality: start at full quality, measure real frame times, step down until
 * 60 fps holds. Chooses by measurement, never by GPU name (iPhones report none).
 *
 * Plain TypeScript, no React (same rule as src/tsl), so a vanilla three.js
 * Experience class could drive it unchanged if G1 drops R3F.
 *
 * Evidence (3 Nov, task 83): cost is mostly per-pixel. Mali Android 60 fps post off,
 * 44 post on, 57 post on at dpr 1.5; Intel gen-11 laptop full screen 2256x1504 at
 * 45 fps with post. So the ladder trades pixels first, then MSAA, then bloom detail,
 * and keeps grain (and the approved look's black void) to the end.
 */

export interface QualitySettings {
  /** Multiplies the base pixel ratio (min(devicePixelRatio, ?dpr cap)). */
  scale: number
  /** MSAA samples on the scene pass. WebGPU allows only 1 or 4, so it's 4 or 0 (then FXAA). */
  msaa: 0 | 4
  /** Bloom internal resolution as a fraction of the drawing buffer (three's default is 0.5). */
  bloomRes: number
  /** Bloom on/off. Off is the floor: grain only. */
  bloom: boolean
}

/** Best first. Each step is cheaper than the one before; the order is the plan's (task 83). */
export const LADDER: readonly QualitySettings[] = [
  { scale: 1, msaa: 4, bloomRes: 0.5, bloom: true }, // L0 full
  { scale: 0.85, msaa: 4, bloomRes: 0.5, bloom: true }, // L1
  { scale: 0.75, msaa: 4, bloomRes: 0.5, bloom: true }, // L2
  { scale: 0.75, msaa: 0, bloomRes: 0.5, bloom: true }, // L3 MSAA -> FXAA
  { scale: 0.75, msaa: 0, bloomRes: 0.25, bloom: true }, // L4 quarter-res bloom
  { scale: 0.6, msaa: 0, bloomRes: 0.25, bloom: true }, // L5
  { scale: 0.6, msaa: 0, bloomRes: 0.25, bloom: false }, // L6 floor: grain only
]

/** Ladder level, clamped to the ladder. */
export function ladderAt(level: number): QualitySettings {
  return LADDER[Math.max(0, Math.min(Math.floor(level), LADDER.length - 1))] ?? LADDER[0]!
}

/** Never render below this many device pixels per CSS pixel: past it, upscaling blur costs more than it saves. */
export const MIN_EFFECTIVE_DPR = 0.75

/** Pixel ratio actually rendered at for a given base ratio and level. Rounded so tiny dpr changes don't thrash. */
export function effectiveDpr(baseDpr: number, s: QualitySettings): number {
  const floor = Math.min(baseDpr, MIN_EFFECTIVE_DPR)
  return Math.max(floor, Math.round(baseDpr * s.scale * 20) / 20)
}

/** What a level actually changes, given whether post is running and the screen's base dpr. */
export function effectiveKey(baseDpr: number, s: QualitySettings, postOn: boolean, bloomOn = true): string {
  const dpr = effectiveDpr(baseDpr, s)
  const bloom = bloomOn && s.bloom ? s.bloomRes : 'off'
  return postOn ? `${dpr}|${s.msaa}|${bloom}` : `${dpr}`
}

export const describeSettings = (s: QualitySettings) =>
  [
    `scale ${s.scale}`,
    s.msaa ? `msaa ${s.msaa}x` : 'fxaa',
    s.bloom ? `bloom ${s.bloomRes === 0.5 ? '½' : s.bloomRes === 0.25 ? '¼' : s.bloomRes}` : 'bloom off',
  ].join(' · ')

// ---------------------------------------------------------------------------
// Governor

export interface GovernorOptions {
  /** Hold this fps. 60 per the plan's budget; a 120 Hz screen just has headroom. */
  targetFps: number
  /** A window counts as failing below this fps (trimmed mean). */
  failFps: number
  /** Ignore everything for this long after the first frame (shader compiles, asset uploads). */
  bootGraceMs: number
  /** Measurement window length. */
  windowMs: number
  /** Consecutive failing windows needed to step down (filters one-off hitches). */
  failWindows: number
  /** Ignore frames for this long after a change (pipeline rebuild, resize). */
  settleMs: number
  /** A step must raise fps by at least this fraction, or it "didn't help". */
  minGain: number
  /** Restore a step when the screen shrinks below its trigger size by this factor. */
  shrinkRestore: number
  /** A frame interval above this is a tab switch or a hitch, not load: the window is dropped. */
  hitchMs: number
}

export const defaultGovernorOptions: GovernorOptions = {
  targetFps: 60,
  failFps: 55,
  bootGraceMs: 2000,
  windowMs: 600,
  failWindows: 2,
  settleMs: 800,
  minGain: 0.04,
  shrinkRestore: 1.35,
  hitchMs: 250,
}

export type GovernorState = 'probing' | 'holding' | 'locked-not-gpu' | 'fixed'

export interface GovernorEvent {
  /** Seconds since boot. */
  t: number
  from: number
  to: number
  fps: number
  reason: 'slow' | 'no-gain-revert' | 'shrink-restore'
}

interface Step {
  /** Level before the step (the way back; may be more than one rung up if rungs were no-ops). */
  from: number
  level: number
  /** fps measured right before this step was taken. */
  fpsBefore: number
  /** Base-dpr drawing-buffer pixels when it was taken. */
  pixels: number
}

/**
 * Feed it one call per frame with the frame interval. It answers with the level to render at.
 * No step-up by measurement: at the 60 fps cap the GPU clocks down, so "headroom" can't be
 * read reliably (see CLAUDE.md). The only way back up is the screen getting smaller.
 */
export class QualityGovernor {
  readonly opts: GovernorOptions
  level = 0
  state: GovernorState = 'probing'
  /** Last window's fps (trimmed mean), for the HUD. */
  fps = 0
  events: GovernorEvent[] = []

  private steps: Step[] = []
  private intervals: number[] = []
  private windowStart = -1
  private ignoreUntil = 0
  private failing = 0
  private failSum = 0
  private bootAt = -1
  /** The step whose fps result is still being judged (did it help?). */
  private pendingJudge: Step | null = null
  private noGainStreak = 0

  constructor(
    private readonly isNoop: (from: number, to: number) => boolean,
    opts: Partial<GovernorOptions> = {},
    fixedLevel: number | null = null,
  ) {
    this.opts = { ...defaultGovernorOptions, ...opts }
    if (fixedLevel !== null) {
      this.level = Math.max(0, Math.min(fixedLevel, LADDER.length - 1))
      this.state = 'fixed'
    }
  }

  /** Call when the page is hidden/shown or anything else invalidates the current window. */
  resetWindow(now: number, extraMs = 0) {
    this.intervals.length = 0
    this.windowStart = -1
    this.failing = 0
    this.failSum = 0
    this.ignoreUntil = Math.max(this.ignoreUntil, now + extraMs)
  }

  /**
   * @param now        performance.now()
   * @param dtMs       interval since the previous frame
   * @param basePixels drawing-buffer pixels at the base dpr (scale 1): CSS w*h*baseDpr^2
   * @returns the level to use (changes only on decisions)
   */
  frame(now: number, dtMs: number, basePixels: number): number {
    if (this.bootAt < 0) {
      this.bootAt = now
      this.ignoreUntil = now + this.opts.bootGraceMs
    }
    if (this.state === 'fixed' || this.state === 'locked-not-gpu') return this.level

    // Screen got much smaller (left full screen, rotated, resized): restore steps taken at bigger sizes.
    const last = this.steps[this.steps.length - 1]
    if (last && basePixels * this.opts.shrinkRestore < last.pixels) {
      this.steps.pop()
      this.change(now, last.from, 'shrink-restore')
      this.pendingJudge = null
      this.noGainStreak = 0
      if (this.state === 'holding') this.state = 'probing'
      return this.level
    }

    if (now < this.ignoreUntil) return this.level
    // A tab switch or a long hitch isn't GPU load: drop the window.
    if (dtMs > this.opts.hitchMs) {
      this.resetWindow(now)
      return this.level
    }

    if (this.windowStart < 0) this.windowStart = now
    this.intervals.push(dtMs)
    if (now - this.windowStart < this.opts.windowMs || this.intervals.length < 12) return this.level

    const fps = trimmedFps(this.intervals)
    this.intervals.length = 0
    this.windowStart = now
    this.fps = fps

    if (fps >= this.opts.failFps) {
      // Holding the target. A pending step obviously helped.
      this.failing = 0
      this.failSum = 0
      this.pendingJudge = null
      this.noGainStreak = 0
      this.state = 'holding'
      return this.level
    }

    this.failing++
    this.failSum += fps
    if (this.failing < this.opts.failWindows) return this.level

    // failWindows slow windows in a row: their mean is this level's measured fps.
    const measured = this.failSum / this.failing
    this.failing = 0
    this.failSum = 0

    if (this.pendingJudge) {
      const step = this.pendingJudge
      this.pendingJudge = null
      const helped = measured > step.fpsBefore * (1 + this.opts.minGain)
      this.noGainStreak = helped ? 0 : this.noGainStreak + 1
      const first = this.steps[0]
      const totalGainLow = first !== undefined && this.steps.length >= 3 && measured < first.fpsBefore * 1.1
      if (this.noGainStreak >= 2 || totalGainLow) {
        // Cheaper pixels bought nothing: not GPU-bound (CPU, a 30 Hz low-power cap, a throttled
        // webview). Give the quality back and stop, rather than blur the art for no gain.
        const back = totalGainLow ? first : this.steps[this.steps.length - 2]
        const revertTo = back?.from ?? this.level
        this.steps.splice(totalGainLow ? 0 : -2)
        this.change(now, revertTo, 'no-gain-revert', measured)
        this.state = 'locked-not-gpu'
        return this.level
      }
    }

    let next = this.level + 1
    while (next < LADDER.length - 1 && this.isNoop(this.level, next)) next++
    if (next >= LADDER.length || this.isNoop(this.level, next)) {
      this.state = 'holding' // at the floor: nothing cheaper left
      return this.level
    }
    const step: Step = { from: this.level, level: next, fpsBefore: measured, pixels: basePixels }
    this.steps.push(step)
    this.pendingJudge = step
    this.change(now, next, 'slow', measured)
    return this.level
  }

  private change(now: number, to: number, reason: GovernorEvent['reason'], fps = this.fps) {
    to = Math.max(0, Math.min(to, LADDER.length - 1))
    this.events.push({ t: (now - this.bootAt) / 1000, from: this.level, to, fps, reason })
    if (this.events.length > 12) this.events.shift()
    this.level = to
    if (reason === 'slow') this.state = 'probing'
    this.resetWindow(now, this.opts.settleMs)
  }
}

/** fps from frame intervals, dropping the slowest 5% so one GC or HUD hitch doesn't count as load. */
export function trimmedFps(intervals: readonly number[]): number {
  const sorted = [...intervals].sort((a, b) => a - b)
  const keep = Math.max(1, Math.floor(sorted.length * 0.95))
  let sum = 0
  for (let i = 0; i < keep; i++) sum += sorted[i] ?? 0
  return (1000 * keep) / sum
}
